import React from 'react';

type SkeletonVariant = 'card' | 'line' | 'circle';

interface LoadingSkeletonProps {
  /** Number of skeleton items to render */
  count?: number;
  /** Variant shape */
  variant?: SkeletonVariant;
  /** Grid columns (1, 2, or 3) */
  columns?: 1 | 2 | 3;
  /** Height in rem for line variant */
  height?: string;
  /** Additional class */
  className?: string;
}

/**
 * Reusable loading skeleton placeholder.
 * Consolidates the repeated skeleton patterns used across pages
 * (Dashboard, SerialList, Notes, CharacterLibrary, Analytics, etc.)
 *
 * @example
 * <LoadingSkeleton count={6} columns={3} variant="card" />
 * <LoadingSkeleton count={3} variant="line" height="2.5rem" />
 */
const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  count = 3,
  variant = 'card',
  columns = 2,
  height,
  className = '',
}) => {
  const gridClass =
    columns === 3
      ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      : columns === 2
        ? 'grid-cols-1 md:grid-cols-2'
        : 'grid-cols-1';

  const itemClass =
    variant === 'card'
      ? `h-44 bg-surface-container animate-pulse border border-outline-variant/5`
      : variant === 'circle'
        ? 'w-10 h-10 bg-surface-container animate-pulse rounded-full border border-outline-variant/5'
        : `bg-surface-container animate-pulse border border-outline-variant/5 ${height ? '' : 'h-10'}`;

  return (
    <div className={`grid ${gridClass} gap-4 ${className}`}>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className={itemClass}
          style={height ? { height } : undefined}
        />
      ))}
    </div>
  );
};

export default LoadingSkeleton;